import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import api from '../api/axios';
import useLocale from '../hooks/useLocale';
import useUpdateBreadcrumbs from '../hooks/useUpdateBreadcrumbs';
import './CombinedSectionManagement.css';
import HelpButton from '../components/HelpButton';

const CombinedSectionManagement = () => {
  const navigate = useNavigate();
  // route: /management/publishing/:festschrift_id/sections/:section_id/articles
  // we treat route param section_id as festschrift_section_id in the logic
  const { festschrift_id, section_id } = useParams();
  const festschrift_section_id = section_id;
  const location = useLocation();
  const { getCurrentLanguage, getLocalizedName, getLocalizedFullName } = useLocale();
  const lang = getCurrentLanguage();

  // --- ACR (slug-based routing) resolver ---
  const [managementOptions, setManagementOptions] = useState(null);

  // Prefer slug once we have it
  const festIdent = managementOptions?.festschrift?.slug || festschrift_id;

  // Resolve section object from managementOptions (works for both incoming id and incoming slug)
  const resolvedSection = useMemo(() => {
    const list = managementOptions?.manageableSections || [];
    const raw = String(section_id || '');
    const num = Number(raw);

    return (
      list.find((s) => s?.slug === raw) ||
      list.find((s) => String(s?.id) === raw) ||
      (Number.isFinite(num) ? list.find((s) => s?.id === num) : null) ||
      null
    );
  }, [managementOptions, section_id]);

  // This is what we want to show in the URL
  const sectionIdent = resolvedSection?.slug || section_id;

  // Numeric ids for legacy /rates endpoints (they are id-based)
  const festRatesId = managementOptions?.festschrift?.id || null;
  const sectionRatesId = resolvedSection?.id || null;

  const searchParams = new URLSearchParams(location.search);
  const confNameFromQuery = searchParams.get('conferenceName') || '';
  const sectionNameFromQuery = searchParams.get('sectionName') || '';

  const festCacheKey = `fest_name_${festschrift_id}`;
  const sectCacheKey = `sect_name_${festschrift_id}_${section_id}`;

  const cachedConfName = (() => {
    try {
      return sessionStorage.getItem(festCacheKey) || '';
    } catch {
      return '';
    }
  })();

  const cachedSectionName = (() => {
    try {
      return sessionStorage.getItem(sectCacheKey) || '';
    } catch {
      return '';
    }
  })();

  const [confNameResolved, setConfNameResolved] = useState(
    confNameFromQuery || cachedConfName || '',
  );

  const [sectionNameResolved, setSectionNameResolved] = useState(
    sectionNameFromQuery || cachedSectionName || '',
  );

  // keep cache in sync with query (if present)
  useEffect(() => {
    if (confNameFromQuery) {
      try {
        sessionStorage.setItem(festCacheKey, confNameFromQuery);
      } catch {
        //TODO: add handling
      }
      setConfNameResolved(confNameFromQuery);
    }
    if (sectionNameFromQuery) {
      try {
        sessionStorage.setItem(sectCacheKey, sectionNameFromQuery);
      } catch {
        //TODO: add handling
      }
      setSectionNameResolved(sectionNameFromQuery);
    }
  }, [confNameFromQuery, sectionNameFromQuery, festCacheKey, sectCacheKey]);

  // Load management options to resolve canonical slug + numeric ids
  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const { data } = await api.get(`/management/publishing/${festschrift_id}`);
        if (cancelled) return;
        setManagementOptions(data || null);
      } catch (e) {
        console.error('Failed to resolve management options for ACR', e);
        if (!cancelled) setManagementOptions(null);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [festschrift_id]);

  // Canonicalize URL to show slugs in the browser (ACR)
  useEffect(() => {
    const desiredFest = managementOptions?.festschrift?.slug;
    const desiredSect = resolvedSection?.slug;

    // If we don't know slugs yet, do nothing.
    if (!desiredFest || !desiredSect) return;

    const desiredPath = `/management/publishing/${desiredFest}/sections/${desiredSect}/articles`;

    if (location.pathname !== desiredPath) {
      navigate(desiredPath + location.search, { replace: true });
    }
  }, [managementOptions, resolvedSection, location.pathname, location.search, navigate]);

  // --- Theses (articles) state ---
  const [thesises, setThesises] = useState([]);
  const [loadingThesises, setLoadingThesises] = useState(true);
  const [thesisError, setThesisError] = useState('');
  const [thesisSearchQuery, setThesisSearchQuery] = useState('');
  const [thesisSortOrder, setThesisSortOrder] = useState('none'); // none | asc | desc
  const [showBulkDownloadModal, setShowBulkDownloadModal] = useState(false);
  const [bulkDownloadScope, setBulkDownloadScope] = useState('section');
  const [bulkDownloadFilter, setBulkDownloadFilter] = useState('latest');
  const [bulkDownloadFileType, setBulkDownloadFileType] = useState('docx');
  const [bulkDownloadSubmitting, setBulkDownloadSubmitting] = useState(false);
  const [bulkDownloadProgress, setBulkDownloadProgress] = useState(0);
  const [bulkDownloadResult, setBulkDownloadResult] = useState(null);

  // --- Section reviewers state ---
  const [reviewers, setReviewers] = useState([]);
  const [loadingReviewers, setLoadingReviewers] = useState(true);
  const [reviewersError, setReviewersError] = useState('');

  // --- Election state ---
  const [electionStatus, setElectionStatus] = useState(null);
  const [loadingElection, setLoadingElection] = useState(false);
  const [electionError, setElectionError] = useState('');
  const [showElectionModal, setShowElectionModal] = useState(false);
  const [electionDateInput, setElectionDateInput] = useState('');

  // --- Modals / confirmations ---
  const [confirmData, setConfirmData] = useState(null);
  const [addModalThesisId, setAddModalThesisId] = useState(null); // добавление к конкретной работе
  const [showSectionAddModal, setShowSectionAddModal] = useState(false); // добавление в секцию

  // --- Lock/Unlock article modal ---
  const [blockModalThesis, setBlockModalThesis] = useState(null);
  const [blockModalMode, setBlockModalMode] = useState('lock'); // 'lock' | 'unlock'
  const [blockStats, setBlockStats] = useState(null);
  const [blockStatsLoading, setBlockStatsLoading] = useState(false);
  const [blockStatsError, setBlockStatsError] = useState('');
  const [blockSubmitting, setBlockSubmitting] = useState(false);

  // local cache of locked article ids (backed by article.is_locked from backend list endpoint)
  const [blockedThesisIds, setBlockedThesisIds] = useState(() => new Set());

  // --- Общий пул доступных кандидатов ---
  const [availableParticipants, setAvailableParticipants] = useState([]);
  const [availableLoading, setAvailableLoading] = useState(false);
  const [availableError, setAvailableError] = useState('');

  // --- Automatic assignment state ---
  const [assignmentStatus, setAssignmentStatus] = useState('not_started'); // not_started | running | failed
  const [assignError, setAssignError] = useState('');
  const [reviewersPerThesis, setReviewersPerThesis] = useState(1);
  const [maxDiff, setMaxDiff] = useState(0);
  const assignmentTimerRef = useRef(null);

  // --- Table of reviewers per thesis ---
  const [thesisReviewersTable, setThesisReviewersTable] = useState({});

  // --- Sorting for section reviewers list ---
  const [sortConfig, setSortConfig] = useState({ key: 'reviewer', direction: 'asc' });

  // --- Breadcrumbs ---
  const resetBreadcrumbs = useCallback(
    (search) => {
      const params = new URLSearchParams(search);

      const confFromQuery = params.get('conferenceName') || '';
      const confCached = (() => {
        try {
          return sessionStorage.getItem(`fest_name_${festschrift_id}`) || '';
        } catch {
          return '';
        }
      })();
      const conf = confFromQuery || confCached || '';

      const sectFromQuery = params.get('sectionName') || '';
      const sectCached = (() => {
        try {
          return sessionStorage.getItem(`sect_name_${festschrift_id}_${section_id}`) || '';
        } catch {
          return '';
        }
      })();
      const sect = sectFromQuery || sectCached || '';

      const encConf = encodeURIComponent(conf);

      return [
        {
          label: conf,
          url: `/management/publishing/${festschrift_id}?conferenceName=${encConf}`,
        },
        { label: sect },
      ];
    },
    [festIdent, sectionNameResolved],
  );
  useUpdateBreadcrumbs(resetBreadcrumbs);

  // --- Helpers ---

  const getThesisName = (titleObj, primaryLanguage) => {
    if (!titleObj) return '';

    const pl = (primaryLanguage || '').toString().trim().toLowerCase();
    const pickByPL = (obj) => {
      if (!obj || typeof obj !== 'object') return null;
      if (pl && (pl === 'ru' || pl === 'en') && typeof obj[pl] === 'string' && obj[pl].trim()) {
        return obj[pl];
      }
      return null;
    };

    // 1) структура { title: { name_ru, name_en } }
    if (
      typeof titleObj === 'object' &&
      (typeof titleObj.name_ru === 'string' || typeof titleObj.name_en === 'string')
    ) {
      const asMap = {
        ru: titleObj.name_ru || '',
        en: titleObj.name_en || '',
      };
      return pickByPL(asMap) || getLocalizedName(asMap);
    }

    // 2) структура { title: { name: { ru, en } } }
    if (titleObj.name && typeof titleObj.name === 'object') {
      return pickByPL(titleObj.name) || getLocalizedName(titleObj.name);
    }

    // 3) просто объект { ru, en }
    if (typeof titleObj === 'object') {
      return pickByPL(titleObj) || getLocalizedName(titleObj);
    }

    // 4) fallback - строка
    return String(titleObj);
  };

  const getArticleLanguageCode = (raw) => {
    if (!raw) return '';
    const s = String(raw).trim().toLowerCase();
    if (!s) return '';
    if (s.startsWith('ru')) return 'RU';
    if (s.startsWith('en')) return 'EN';
    return s.toUpperCase();
  };

  const renderArticleLanguageBadge = (raw) => {
    const code = getArticleLanguageCode(raw);
    if (!code) return null;
    const label = lang === 'ru' ? `Язык статьи: ${code}` : `Article language: ${code}`;
    return <span className="article-lang-badge">{label}</span>;
  };

  const renderAuthorLine = (thesis) => {
    const author = thesis?.author;
    if (!author) return null;

    const name = author.full_name ? getLocalizedFullName(author.full_name) : '';
    const email = author.email || '';

    if (!name && !email) return null;

    return (
      <div className="thesis-author">
        <span className="thesis-author-label">{lang === 'ru' ? 'Автор:' : 'Author:'}</span>
        {name && <span className="thesis-author-name">{name}</span>}
        {email && <span className="thesis-author-email">{email}</span>}
      </div>
    );
  };

  const goToThesisManagement = (thesisId) => {
    const t = thesises.find((x) => x.id === thesisId);
    const name = t ? getThesisName(t.title, t.primary_language) : '';
    navigate(
      `/management/publishing/${festIdent}/sections/${sectionIdent}/articles/${thesisId}` +
        `?conferenceName=${encodeURIComponent(confNameResolved)}` +
        `&sectionName=${encodeURIComponent(sectionNameResolved)}` +
        `&thesisTitle=${encodeURIComponent(name)}`,
    );
  };

  const formatDate = (val) => {
    if (!val) return '';
    try {
      return new Date(val).toLocaleDateString();
    } catch {
      return String(val);
    }
  };

  // --- Fetching data ---

  const fetchThesises = useCallback(async () => {
    try {
      setLoadingThesises(true);
      const { data } = await api.get(
        `/management/publishing/${festIdent}/sections/${sectionIdent}/articles`,
      );

      setThesises(data || []);
      setThesisError('');

      // Derive locked state once from list payload to avoid per-row /block-stats calls.
      const lockedIds = new Set((data || []).filter((a) => a?.is_locked === true).map((a) => a.id));
      setBlockedThesisIds(lockedIds);
    } catch (err) {
      console.error(err);
      setThesisError(lang === 'ru' ? 'Ошибка при загрузке статей' : 'Error loading articles');
    } finally {
      setLoadingThesises(false);
    }
  }, [festIdent, sectionIdent, lang]);

  const fetchThesisReviewerRows = useCallback(
    async (thesisId) => {
      try {
        const { data } = await api.get(
          `/management/publishing/${festIdent}/sections/${sectionIdent}/articles/${thesisId}/reviewers`,
        );
        setThesisReviewersTable((prev) => ({
          ...prev,
          [thesisId]: data.rows || [],
        }));
      } catch (err) {
        console.error('Failed to load reviewers table for thesis', thesisId, err);
        setThesisReviewersTable((prev) => ({ ...prev, [thesisId]: [] }));
      }
    },
    [festIdent, sectionIdent],
  );

  const loadAllThesisReviewerTables = useCallback(async () => {
    const ids = (thesises || []).map((t) => t.id);
    if (!ids.length) return;
    try {
      await Promise.all(ids.map((id) => fetchThesisReviewerRows(id)));
    } catch (err) {
      console.error(err);
    }
  }, [thesises, fetchThesisReviewerRows]);

  const fetchGlobalReviewers = useCallback(async () => {
    if (!sectionRatesId) return;
    try {
      setLoadingReviewers(true);
      const { data } = await api.get(`/rates/section/${sectionRatesId}/current`);
      setReviewers(
        data.map((r) => ({
          reviewer_id: r.id,
          reviewer_full_name: getLocalizedFullName(r.reviewer_full_name),
          reviewer_email: r.reviewer_email,
          status: r.status,
          details: r.details,
        })),
      );
      setReviewersError('');
    } catch (err) {
      console.error(err);
      setReviewersError(
        lang === 'ru'
          ? 'Ошибка при загрузке текущих рецензентов'
          : 'Error loading current reviewers',
      );
    } finally {
      setLoadingReviewers(false);
    }
  }, [sectionRatesId, getLocalizedFullName, lang]);

  const fetchElectionStatus = useCallback(async () => {
    if (!sectionRatesId) return;
    try {
      setLoadingElection(true);
      const { data } = await api.get(`/rates/section/${sectionRatesId}/elections/status`);
      setElectionStatus(data);
      setElectionError('');
    } catch (err) {
      console.error(err);
      setElectionError(
        lang === 'ru' ? 'Ошибка при загрузке статуса голосования' : 'Error loading election status',
      );
    } finally {
      setLoadingElection(false);
    }
  }, [sectionRatesId, lang]);

  useEffect(() => {
    fetchThesises();

    if (sectionRatesId) {
      fetchGlobalReviewers();
      fetchElectionStatus();

      // состояние задачи авто-назначения
      api
        .get(`/rates/section/${sectionRatesId}/rates/current`)
        .then(({ data }) => {
          const s = data?.status;
          if (s === 'failed') {
            setAssignError(data.details || '');
            setAssignmentStatus('failed');
          } else if (s === 'running') {
            setAssignmentStatus('running');
          } else {
            setAssignmentStatus('not_started');
          }
        })
        .catch(() => {
          // игнорим
        });
    }
  }, [fetchThesises, fetchGlobalReviewers, fetchElectionStatus, sectionRatesId]);

  // После загрузки тезисов - подгружаем таблицы ревьюеров
  useEffect(() => {
    if (thesises.length) {
      loadAllThesisReviewerTables();
    }
  }, [thesises, loadAllThesisReviewerTables]);

  // Загрузка общего пула при открытии любого модального окна добавления
  useEffect(() => {
    const thesisModalOpen = addModalThesisId != null;
    const sectionModalOpen = showSectionAddModal;

    if (!sectionModalOpen && !thesisModalOpen) return;

    // /rates is strictly id-based (sectionRatesId must be resolved)
    if (sectionModalOpen && !sectionRatesId) return;

    setAvailableLoading(true);
    setAvailableError('');

    const request = thesisModalOpen
      ? api.get(`/auth/candidates/${addModalThesisId}`) // candidates for THIS article only
      : api.get(`/rates/section/${sectionRatesId}/available`); // candidates for adding to section

    request
      .then(({ data }) => {
        setAvailableParticipants(
          (data || []).map((item) => ({
            id: item.id,
            fullName: getLocalizedFullName(item.reviewer_full_name),
            email: item.reviewer_email,
          })),
        );
      })
      .catch((err) => {
        console.error(err);
        setAvailableError(
          lang === 'ru'
            ? 'Ошибка загрузки списка возможных рецензентов'
            : 'Error loading available reviewers',
        );
      })
      .finally(() => setAvailableLoading(false));
  }, [showSectionAddModal, addModalThesisId, sectionRatesId, getLocalizedFullName, lang]);

  // --- Sorting & filtering ---

  const handleSortThesis = () => {
    if (thesisSortOrder === 'none') setThesisSortOrder('asc');
    else if (thesisSortOrder === 'asc') setThesisSortOrder('desc');
    else setThesisSortOrder('none');
  };

  const filteredThesises = useMemo(() => {
    const q = thesisSearchQuery.toLowerCase();
    return thesises.filter((t) => getThesisName(t.title, t.primary_language).toLowerCase().includes(q));
  }, [thesises, thesisSearchQuery]);

  const sortedThesises = useMemo(() => {
    if (thesisSortOrder === 'none') return filteredThesises;
    return [...filteredThesises].sort((a, b) => {
      const A = getThesisName(a.title, a.primary_language).toLowerCase();
      const B = getThesisName(b.title, b.primary_language).toLowerCase();
      if (A < B) return thesisSortOrder === 'asc' ? -1 : 1;
      if (A > B) return thesisSortOrder === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredThesises, thesisSortOrder]);

  const handleSortReviewers = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        if (prev.direction === 'asc') {
          return { key, direction: 'desc' };
        }
        if (prev.direction === 'desc') {
          return { key: 'reviewer', direction: 'asc' };
        }
        return { key, direction: 'asc' };
      }
      return { key, direction: 'asc' };
    });
  };

  const sortedReviewers = useMemo(() => {
    const list = [...reviewers];
    if (!sortConfig.key) return list;
    const key = sortConfig.key;
    const dir = sortConfig.direction === 'desc' ? -1 : 1;

    return list.sort((a, b) => {
      const aVal =
        key === 'email' ? a.reviewer_email.toLowerCase() : a.reviewer_full_name.toLowerCase();
      const bVal =
        key === 'email' ? b.reviewer_email.toLowerCase() : b.reviewer_full_name.toLowerCase();
      if (aVal < bVal) return -1 * dir;
      if (aVal > bVal) return 1 * dir;
      return 0;
    });
  }, [reviewers, sortConfig]);

  // --- Add / delete reviewers ---

  const addReviewerToThesis = async (thesisId, reviewerId) => {
    try {
      await api.post(
        `/management/publishing/${festIdent}/sections/${sectionIdent}/articles/${thesisId}/reviewers/${reviewerId}`,
      );
      alert(lang === 'ru' ? 'Приглашение отправлено' : 'Invitation sent');
    } catch (err) {
      console.error(err);
      const status = err?.response?.status;

      if (status === 409) {
        alert(get409AssignReviewerMessage(err));
      } else {
        alert(lang === 'ru' ? 'Ошибка отправки приглашения' : 'Failed to send invitation');
      }
    } finally {
      await fetchThesises();
      await loadAllThesisReviewerTables();
    }
  };

  const deleteReviewerFromThesis = async (thesisId, reviewerId) => {
    const deleteReview = api
      .delete(`/management/publishing/${festIdent}/sections/${sectionIdent}/articles/${thesisId}`, {
        params: { reviewer_id: reviewerId },
      })
      .catch(() => {});

    const deleteInvitation = api
      .delete(
        `/management/publishing/${festIdent}/sections/${sectionIdent}/articles/${thesisId}/reviewers/${reviewerId}`,
      )
      .catch(() => {});

    try {
      await Promise.all([deleteReview, deleteInvitation]);
      alert(lang === 'ru' ? 'Удалено' : 'Removed');
    } catch (err) {
      console.error(err);
      alert(lang === 'ru' ? 'Ошибка удаления' : 'Failed to remove');
    } finally {
      await fetchThesises();
      await loadAllThesisReviewerTables();
    }
  };

  const addReviewerToSection = async (reviewerId) => {
    try {
      await api.post(`/rates/section/${sectionRatesId}`, null, {
        params: { reviewer_id: reviewerId },
      });
      alert(lang === 'ru' ? 'Рецензент добавлен в секцию' : 'Reviewer added to section');
    } catch (err) {
      console.error(err);
      const status = err?.response?.status;

      if (status === 409) {
        alert(get409AssignReviewerMessage(err));
      } else {
        alert(lang === 'ru' ? 'Ошибка добавления рецензента' : 'Error adding reviewer');
      }
    } finally {
      await fetchGlobalReviewers();
    }
  };

  const deleteReviewerFromSection = async (reviewerId) => {
    try {
      await api.delete(`/rates/section/${sectionRatesId}?reviewer_id=${reviewerId}`);
      alert(lang === 'ru' ? 'Рецензент удалён из секции' : 'Reviewer removed from section');
    } catch (err) {
      console.error(err);
      alert(lang === 'ru' ? 'Ошибка удаления рецензента' : 'Error removing reviewer');
    } finally {
      await fetchGlobalReviewers();
    }
  };

  // --- Confirmation modal helpers ---

  const openConfirmModal = (message, onConfirm, onCancel) => {
    setConfirmData({ message, onConfirm, onCancel });
  };

  const closeConfirmModal = () => {
    setConfirmData(null);
  };

  // --- Download helpers (used by Block Article modal) ---
  const downloadBlob = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || 'download';
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => window.URL.revokeObjectURL(url), 1000);
  };

  const downloadFromUrl = async (url, fallbackFilename) => {
    if (!url) return;

    try {
      const res = await api.get(url, { responseType: 'blob' });

      // Try to read filename from Content-Disposition, fallback
      const cd = res.headers?.['content-disposition'] || '';
      const match = cd.match(/filename\*=UTF-8''([^;]+)|filename="([^"]+)"/i);
      const filename = decodeURIComponent(
        match?.[1] || match?.[2] || fallbackFilename || 'download',
      );

      downloadBlob(new Blob([res.data]), filename);
    } catch (err) {
      console.error(err);
      alert(lang === 'ru' ? 'Не удалось скачать файл' : 'Failed to download file');
    }
  };


  const openBulkDownloadModal = () => {
    setBulkDownloadSubmitting(false);
    setBulkDownloadProgress(0);
    setBulkDownloadResult(null);
    setShowBulkDownloadModal(true);
  };

  const closeBulkDownloadModal = () => {
    if (bulkDownloadSubmitting) return;
    setShowBulkDownloadModal(false);
    setBulkDownloadProgress(0);
    setBulkDownloadResult(null);
  };

  const handleBulkDownload = async () => {
    setBulkDownloadSubmitting(true);
    setBulkDownloadProgress(0);
    setBulkDownloadResult(null);

    try {
      const res = await api.get(
        `/management/publishing/${festIdent}/sections/${sectionIdent}/articles/download`,
        {
          params: {
            scope: bulkDownloadScope,
            review_filter: bulkDownloadFilter,
            file_type: bulkDownloadFileType,
          },
          responseType: 'blob',
          onDownloadProgress: (event) => {
            if (typeof event?.progress === 'number' && Number.isFinite(event.progress)) {
              setBulkDownloadProgress(Math.max(0, Math.min(100, Math.round(event.progress * 100))));
              return;
            }
            if (event?.total && event?.loaded != null) {
              setBulkDownloadProgress(Math.max(0, Math.min(100, Math.round((event.loaded / event.total) * 100))));
              return;
            }
            setBulkDownloadProgress((prev) => (prev == null || prev >= 95 ? prev : prev + 5));
          },
        },
      );

      const cd = res.headers?.['content-disposition'] || '';
      const match = cd.match(/filename\*=UTF-8''([^;]+)|filename="([^"]+)"/i);
      const filename = decodeURIComponent(match?.[1] || match?.[2] || 'articles.zip');
      const failureCount = Number(res.headers?.['x-download-failures-count'] || 0);
      const successCount = Number(res.headers?.['x-download-success-count'] || 0);
      const issuesIncluded = (res.headers?.['x-download-issues-included'] || '').toLowerCase() === 'true';

      setBulkDownloadProgress(100);
      downloadBlob(new Blob([res.data]), filename);
      setBulkDownloadResult({
        type: failureCount > 0 ? 'warning' : 'success',
        message:
          failureCount > 0
            ? lang === 'ru'
              ? `Архив скачан. Успешно: ${successCount}. Не удалось добавить файлов: ${failureCount}. Подробности сохранены в download_issues.txt.`
              : `Archive downloaded. Added: ${successCount}. Failed to include: ${failureCount}. Details are saved in download_issues.txt.`
            : lang === 'ru'
              ? `Архив скачан. Файлов добавлено: ${successCount}.`
              : `Archive downloaded. Files added: ${successCount}.`,
        issuesIncluded,
      });
    } catch (err) {
      console.error(err);
      const detail = err?.response?.data?.detail;
      setBulkDownloadResult({
        type: 'error',
        message:
          detail ||
          (lang === 'ru'
            ? 'Не удалось сформировать архив для скачивания'
            : 'Failed to prepare the download archive'),
      });
    } finally {
      setBulkDownloadSubmitting(false);
    }
  };

  const setThesisLockedState = useCallback((articleId, locked) => {
    setBlockedThesisIds((prev) => {
      const next = new Set(prev);
      if (locked) next.add(articleId);
      else next.delete(articleId);
      return next;
    });

    // Keep thesises array in sync so UI stays consistent without refetch.
    setThesises((prev) =>
      (prev || []).map((t) => (t.id === articleId ? { ...t, is_locked: locked } : t)),
    );
  }, []);

  const loadBlockStats = useCallback(
    async (articleId) => {
      setBlockStatsLoading(true);
      setBlockStatsError('');
      setBlockStats(null);

      try {
        const { data } = await api.get(
          `/management/publishing/${festIdent}/sections/${sectionIdent}/articles/${articleId}/block-stats`,
        );
        setBlockStats(data);

        if (data?.is_finalized || data?.finalized || data?.blocked || data?.is_locked) {
          setThesisLockedState(articleId, true);
        }
      } catch (err) {
        console.error(err);
        setBlockStatsError(
          lang === 'ru' ? 'Не удалось загрузить статистику' : 'Failed to load statistics',
        );
      } finally {
        setBlockStatsLoading(false);
      }
    },
    [festIdent, sectionIdent, lang, setThesisLockedState],
  );

  const openBlockArticleModal = (thesis) => {
    const currentlyLocked = blockedThesisIds.has(thesis.id);
    setBlockModalMode(currentlyLocked ? 'unlock' : 'lock');
    setBlockModalThesis(thesis);
    loadBlockStats(thesis.id);
  };

  const closeBlockArticleModal = () => {
    setBlockModalThesis(null);
    setBlockModalMode('lock');
    setBlockStats(null);
    setBlockStatsError('');
    setBlockStatsLoading(false);
    setBlockSubmitting(false);
  };

  const submitToggleLockArticle = async () => {
    if (!blockModalThesis) return;

    const articleId = blockModalThesis.id;
    const isUnlock = blockModalMode === 'unlock';

    setBlockSubmitting(true);
    setBlockStatsError('');

    try {
      const url = isUnlock
        ? `/management/publishing/${festIdent}/sections/${sectionIdent}/articles/${articleId}/unlock`
        : `/management/publishing/${festIdent}/sections/${sectionIdent}/articles/${articleId}/block`;

      await api.post(url);

      setThesisLockedState(articleId, !isUnlock);
      closeBlockArticleModal();
    } catch (err) {
      console.error(err);
      const detail = err?.response?.data?.detail;

      setBlockStatsError(
        detail ||
          (lang === 'ru'
            ? isUnlock
              ? 'Не удалось разблокировать статью'
              : 'Не удалось заблокировать статью'
            : isUnlock
              ? 'Failed to unlock article'
              : 'Failed to lock article'),
      );
    } finally {
      setBlockSubmitting(false);
    }
  };

  const handleDeleteThesisReviewer = (thesisId, row) => {
    const nameForConfirm = getLocalizedFullName(row.reviewer_full_name);
    openConfirmModal(
      lang === 'ru'
        ? `Удалить назначение/приглашение для ${nameForConfirm}?`
        : `Remove assignment/invitation for ${nameForConfirm}?`,
      async () => {
        await deleteReviewerFromThesis(thesisId, row.reviewer_id);
        await fetchThesises();
        await loadAllThesisReviewerTables();
        closeConfirmModal();
      },
      closeConfirmModal,
    );
  };

  const handleDeleteSectionReviewer = (r) => {
    openConfirmModal(
      lang === 'ru'
        ? `Удалить рецензента ${r.reviewer_full_name} из секции?`
        : `Remove reviewer ${r.reviewer_full_name} from section?`,
      async () => {
        await deleteReviewerFromSection(r.reviewer_id);
        closeConfirmModal();
      },
      closeConfirmModal,
    );
  };

  // --- Election open/close ---

  const handleCloseElection = () => {
    if (!electionStatus) return;
    openConfirmModal(
      lang === 'ru'
        ? 'Вы уверены, что хотите закрыть голосование?'
        : 'Are you sure you want to close the election?',
      async () => {
        await api.post(`/rates/section/${sectionRatesId}/elections/close`, null, {
          params: {
            elections_till_date: electionStatus.election_till_date,
          },
        });
        await fetchElectionStatus();
        closeConfirmModal();
      },
      closeConfirmModal,
    );
  };

  const handleOpenElection = () => {
    setShowElectionModal(true);
  };

  const confirmOpenElection = async () => {
    if (!electionDateInput) {
      alert(lang === 'ru' ? 'Введите дату' : 'Enter a date');
      return;
    }
    try {
      await api.post(`/rates/section/${sectionRatesId}/elections/open`, null, {
        params: { elections_till_date: electionDateInput },
      });
      await fetchElectionStatus();
      setShowElectionModal(false);
      setElectionDateInput('');
    } catch (err) {
      console.error(err);
      alert(lang === 'ru' ? 'Не удалось открыть голосование' : 'Failed to open election');
    }
  };

  const get409AssignReviewerMessage = (err) => {
    const detailRaw = err?.response?.data?.detail;

    // backend usually sends a string, but be defensive
    const detail =
      typeof detailRaw === 'string' ? detailRaw : detailRaw?.message || detailRaw?.code || '';

    const d = (detail || '').toLowerCase();

    if (d.includes('finalized')) {
      return lang === 'ru'
        ? 'Работа над этой статьей уже завершена. Нельзя назначать новых рецензентов.'
        : 'This article is already finalized. You cannot assign new reviewers.';
    }

    if (d.includes('double-blind') || d.includes('double_blind')) {
      return lang === 'ru'
        ? 'В формате double-blind пользователь не может быть одновременно рецензентом и редактором'
        : 'In double-blind review a user cannot be both reviewer and editor';
    }

    // fallback (don’t lie to the user)
    return detail || (lang === 'ru' ? 'Конфликт (409)' : 'Conflict (409)');
  };

  // --- Automatic assignment ---

  const startAssignment = async () => {
    setAssignmentStatus('running');
    setAssignError('');
    try {
      if (!festRatesId || !sectionRatesId) {
        setAssignError(
          lang === 'ru'
            ? 'Не удалось определить секцию/сборник'
            : 'Failed to resolve festschrift/section',
        );
        setAssignmentStatus('failed');
        return;
      }

      await api.post(
        `/rates/publishing/${festRatesId}/sections/${sectionRatesId}/rates/start`,
        null,
        {
          params: {
            reviewers_per_article: reviewersPerThesis,
            max_diff: maxDiff,
          },
        },
      );
    } catch (err) {
      console.error(err);
      setAssignError(lang === 'ru' ? 'Не удалось запустить задачу' : 'Failed to start the task');
      setAssignmentStatus('failed');
    }
  };

  useEffect(() => {
    if (assignmentStatus !== 'running') return;

    assignmentTimerRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/rates/section/${sectionRatesId}/rates/current`);
        const s = data?.status;
        if (s === 'failed') {
          setAssignError(data.details || '');
          setAssignmentStatus('failed');
          return;
        }
        if (s === 'running') {
          setAssignmentStatus('running');
          return;
        }
        // завершено
        setAssignmentStatus('not_started');
        await fetchThesises();
        await fetchGlobalReviewers();
      } catch (err) {
        console.error(err);
        setAssignError(
          lang === 'ru' ? 'Ошибка при опросе статуса задачи' : 'Error polling assignment status',
        );
        setAssignmentStatus('failed');
      }
    }, 3000);

    return () => {
      if (assignmentTimerRef.current) {
        clearTimeout(assignmentTimerRef.current);
      }
    };
  }, [assignmentStatus, festschrift_section_id, fetchThesises, fetchGlobalReviewers, lang]);

  // --- Render ---

  return (
    <div className="combined-management-container">
      {/* Левая колонка: тезисы (статьи) */}
      <div className="thesis-management-section">
        <div className="page-title-row">
          <h1 className="page-title">{lang === 'ru' ? 'Список статей' : 'Article list'}</h1>
          <HelpButton helpId="sections.management.article_list" helpLabel="Подсказка" />
        </div>

        <div className="thesis-toolbar">
          <div className="thesis-search-container">
            <input
              type="text"
              className="thesis-search-input"
              placeholder={lang === 'ru' ? 'Поиск по названию...' : 'Search by title...'}
              value={thesisSearchQuery}
              onChange={(e) => setThesisSearchQuery(e.target.value)}
            />
          </div>
          <div className="thesis-toolbar-actions">
            <button type="button" className="thesis-sort-btn" onClick={handleSortThesis}>
              {lang === 'ru' ? 'Сортировать по названию' : 'Sort by title'}{' '}
              {thesisSortOrder === 'asc' ? '▲' : thesisSortOrder === 'desc' ? '▼' : ''}
            </button>
            <button type="button" className="thesis-download-btn" onClick={openBulkDownloadModal}>
              {lang === 'ru' ? 'Скачать' : 'Download'}
            </button>
          </div>
        </div>

        <div className="thesis-list">
          {loadingThesises ? (
            <div className="loading-state">
              {lang === 'ru' ? 'Загрузка статей...' : 'Loading articles...'}
            </div>
          ) : thesisError ? (
            <div className="error-state">{thesisError}</div>
          ) : sortedThesises.length === 0 ? (
            <div className="loading-state">
              {lang === 'ru' ? 'Статьи не найдены' : 'No articles found'}
            </div>
          ) : (
            sortedThesises.map((thesis) => {
              const rows = thesisReviewersTable[thesis.id] || [];
              return (
                <div key={thesis.id} className="thesis-item">
                  <div className="thesis-header">
                    <div className="thesis-title-wrap">
                      <a
                        className="thesis-title-link"
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          goToThesisManagement(thesis.id);
                        }}
                      >
                        {getThesisName(thesis.title, thesis.primary_language)}
                      </a>
                      {renderArticleLanguageBadge(thesis.primary_language)}
                    </div>

                    <div className="thesis-header-actions">
                      {(() => {
                        const isLocked = blockedThesisIds.has(thesis.id);
                        const tooltip = isLocked
                          ? lang === 'ru'
                            ? 'Разблокировать'
                            : 'Unlock'
                          : lang === 'ru'
                            ? 'Заблокировать'
                            : 'Lock';

                        return (
                          <button
                            type="button"
                            className={`lock-toggle-btn ${isLocked ? 'locked' : ''}`}
                            onClick={() => openBlockArticleModal(thesis)}
                            title={tooltip}
                            aria-label={tooltip}
                          >
                            {isLocked ? '🔒' : '🔓'}
                          </button>
                        );
                      })()}
                    </div>
                  </div>

                  {renderAuthorLine(thesis)}

                  <div className="thesis-reviewers">
                    <ul className="reviewers-ul">
                      {rows.map((row) => (
                        <li
                          key={`unified-${thesis.id}-${row.reviewer_id}`}
                          className="reviewer-item"
                        >
                          <div className="reviewer-info">
                            <span className="reviewer-name">
                              {getLocalizedFullName(row.reviewer_full_name)}
                            </span>
                            <span className="reviewer-email">{row.reviewer_email}</span>
                          </div>

                          <div className="reviewer-status">
                            {row.review_present ? (
                              (() => {
                                const st = String(row.status || '').toLowerCase();
                                const byStatus = () => {
                                  if (st === 'approved')
                                    return {
                                      label: lang === 'ru' ? 'Одобрил' : 'Approved',
                                      cls: 'badge badge-success badge-link',
                                    };
                                  if (st === 'revision_required')
                                    return {
                                      label:
                                        lang === 'ru'
                                          ? 'Отправил на доработку'
                                          : 'Revision required',
                                      cls: 'badge badge-warning badge-link',
                                    };
                                  if (st === 'rejected' || st === 'denied')
                                    return {
                                      label: lang === 'ru' ? 'Отклонил' : 'Rejected',
                                      cls: 'badge badge-error badge-link',
                                    };
                                  if (st === 'in_progress')
                                    return {
                                      label: lang === 'ru' ? 'В процессе' : 'In progress',
                                      cls: 'badge badge-neutral badge-link',
                                    };
                                  if (st === 'assigned' || st === 'accepted' || st === 'pending')
                                    return {
                                      label: lang === 'ru' ? 'Назначено' : 'Assigned',
                                      cls: 'badge badge-neutral badge-link',
                                    };
                                  if (st === 'finalized')
                                    return {
                                      label: lang === 'ru' ? 'Закончил' : 'Finalized',
                                      cls: 'badge badge-neutral badge-link',
                                    };

                                  const scoreRaw = row.score;
                                  const score =
                                    scoreRaw === 0 ? 0 : scoreRaw ? Number(scoreRaw) : null;
                                  if (score === 1)
                                    return {
                                      label: lang === 'ru' ? 'Одобрил' : 'Approved',
                                      cls: 'badge badge-success badge-link',
                                    };
                                  if (score === 0)
                                    return {
                                      label:
                                        lang === 'ru'
                                          ? 'Отправил на доработку'
                                          : 'Revision required',
                                      cls: 'badge badge-warning badge-link',
                                    };
                                  if (score === -1)
                                    return {
                                      label: lang === 'ru' ? 'Отклонил' : 'Rejected',
                                      cls: 'badge badge-error badge-link',
                                    };

                                  return {
                                    label:
                                      lang === 'ru' ? 'Рецензия отправлена' : 'Review uploaded',
                                    cls: 'badge badge-neutral badge-link',
                                  };
                                };

                                const { label, cls } = byStatus();

                                return (
                                  <span
                                    className={cls}
                                    onClick={(e) => {
                                      if (!row.review_id) return;
                                      e.stopPropagation();
                                      const name = thesis ? getThesisName(thesis.title, thesis.primary_language) : '';
                                      navigate(
                                        `/publishing/${festIdent}/review/${row.review_id}` +
                                          `?conferenceName=${encodeURIComponent(confNameResolved)}` +
                                          `&sectionName=${encodeURIComponent(sectionNameResolved)}` +
                                          `&thesisTitle=${encodeURIComponent(name)}` +
                                          `&editable=False&flow=management` +
                                          `&conference_id=${festIdent}&section_id=${sectionIdent}&thesis_id=${thesis?.id}`,
                                      );
                                    }}
                                    title={
                                      row.review_id
                                        ? lang === 'ru'
                                          ? 'Открыть комментарии'
                                          : 'Open comments'
                                        : ''
                                    }
                                  >
                                    {label}
                                  </span>
                                );
                              })()
                            ) : (
                              <>
                                {row.status === 'pending' && (
                                  <span className="badge badge-warning">
                                    {lang === 'ru' ? 'Приглашён' : 'Invited'}
                                  </span>
                                )}
                                {row.status === 'accepted' && (
                                  <span className="badge badge-info">
                                    {lang === 'ru' ? 'Принял в работу' : 'Accepted review task'}
                                  </span>
                                )}
                                {row.status === 'rejected' && (
                                  <span className="badge badge-error">
                                    {lang === 'ru' ? 'Отказ от рецензирования' : 'Declined review task'}
                                  </span>
                                )}
                              </>
                            )}
                          </div>

                          <button
                            className="delete-btn delete-btn--small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteThesisReviewer(thesis.id, row);
                            }}
                          >
                            {lang === 'ru' ? 'Удалить' : 'Delete'}
                          </button>
                        </li>
                      ))}
                    </ul>

                    {!blockedThesisIds.has(thesis.id) && (
                      <div className="add-reviewer-btn-container">
                        <button
                          className="add-reviewer-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            setAddModalThesisId(thesis.id);
                          }}
                        >
                          {lang === 'ru' ? 'Добавить рецензента' : 'Add reviewer'}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Вертикальный разделитель */}
      <div className="vertical-separator" />

      {/* Правая колонка: секция, голосование, авто-назначение */}
      <div className="reviewers-management-section">
        {/* Голосование */}
        <div className="election-management-block">
          <h2 className="block-title">{lang === 'ru' ? 'Голосование' : 'Election'}</h2>
          {loadingElection ? (
            <div className="loading-state">
              {lang === 'ru' ? 'Загрузка статуса голосования...' : 'Loading election status...'}
            </div>
          ) : electionError ? (
            <div className="error-state">{electionError}</div>
          ) : electionStatus ? (
            <div className="election-status-info">
              {electionStatus.election_open ? (
                <>
                  <p>
                    {lang === 'ru'
                      ? `Голосование открыто до: ${formatDate(electionStatus.election_till_date)}`
                      : `Election is open until: ${formatDate(electionStatus.election_till_date)}`}
                  </p>
                  <p>
                    {lang === 'ru'
                      ? `Анкет заполнено: ${electionStatus.forms_filled}`
                      : `Forms filled: ${electionStatus.forms_filled}`}
                  </p>
                  <button className="election-action-btn" onClick={handleCloseElection}>
                    {lang === 'ru' ? 'Закрыть голосование' : 'Close election'}
                  </button>
                </>
              ) : (
                <>
                  <p>
                    {lang === 'ru'
                      ? `Голосование закрыто. Анкет заполнено: ${electionStatus.forms_filled}`
                      : `Election is closed. Forms filled: ${electionStatus.forms_filled}`}
                  </p>
                  <button className="election-action-btn" onClick={handleOpenElection}>
                    {lang === 'ru' ? 'Открыть голосование' : 'Open election'}
                  </button>
                </>
              )}
            </div>
          ) : null}
        </div>

        {/* Авто-назначение */}
        <div className="assignment-section">
          <h2 className="block-title">
            {lang === 'ru' ? 'Автоматическое назначение рецензентов' : 'Automatic assignment'}
          </h2>

          {assignmentStatus === 'not_started' && (
            <div className="assignment-form">
              <p>{lang === 'ru' ? `Статей: ${thesises.length}` : `Articles: ${thesises.length}`}</p>
              <p>
                {lang === 'ru'
                  ? `Рецензентов: ${reviewers.length}`
                  : `Reviewers: ${reviewers.length}`}
              </p>

              <label>
                {lang === 'ru' ? 'Рецензентов на статью' : 'Reviewers per article'}
                :
                <input
                  type="number"
                  min="1"
                  value={reviewersPerThesis}
                  onChange={(e) => setReviewersPerThesis(Number(e.target.value) || 1)}
                />
              </label>

              <label>
                {lang === 'ru'
                  ? 'Макс. разница по числу работ'
                  : 'Max difference in number of assignments'}
                :
                <input
                  type="number"
                  min="0"
                  value={maxDiff}
                  onChange={(e) => setMaxDiff(Number(e.target.value) || 0)}
                />
              </label>

              <button className="assign-btn" onClick={startAssignment}>
                {lang === 'ru' ? 'Начать автораспределение' : 'Start assignment'}
              </button>

              {assignError && <p className="error-state">{assignError}</p>}
            </div>
          )}

          {assignmentStatus === 'running' && (
            <div className="loading-state">
              {lang === 'ru' ? 'Идёт автораспределение...' : 'Assignment in progress...'}
            </div>
          )}

          {assignmentStatus === 'failed' && (
            <div className="failed-state">
              <p className="error-state">
                {lang === 'ru'
                  ? `Распределение не удалось: ${assignError}`
                  : `Assignment failed: ${assignError}`}
              </p>
              <button className="assign-btn" onClick={() => setAssignmentStatus('not_started')}>
                {lang === 'ru' ? 'Повторить' : 'Retry'}
              </button>
            </div>
          )}
        </div>

        {/* Текущие рецензенты секции */}
        <div className="current-reviewers-section">
          <h2 className="current-reviewers-section-h2">
            {lang === 'ru' ? 'Текущие рецензенты секции' : 'Section reviewers'}
          </h2>

          {loadingReviewers ? (
            <div className="loading-state">
              {lang === 'ru' ? 'Загрузка данных рецензентов...' : 'Loading reviewer data...'}
            </div>
          ) : reviewersError ? (
            <div className="error-state">{reviewersError}</div>
          ) : sortedReviewers.length > 0 ? (
            <>
              <div className="section-reviewers-toolbar">
                <span className="section-reviewers-toolbar-label">
                  {lang === 'ru' ? 'Сортировка:' : 'Sort by:'}
                </span>
                <button
                  type="button"
                  className={
                    'section-reviewers-sort-btn' +
                    (sortConfig.key === 'reviewer' ? ' section-reviewers-sort-btn--active' : '')
                  }
                  onClick={() => handleSortReviewers('reviewer')}
                >
                  {lang === 'ru' ? 'ФИО' : 'Name'}
                  {sortConfig.key === 'reviewer' && (sortConfig.direction === 'asc' ? ' ▲' : ' ▼')}
                </button>
                <button
                  type="button"
                  className={
                    'section-reviewers-sort-btn' +
                    (sortConfig.key === 'email' ? ' section-reviewers-sort-btn--active' : '')
                  }
                  onClick={() => handleSortReviewers('email')}
                >
                  Email
                  {sortConfig.key === 'email' && (sortConfig.direction === 'asc' ? ' ▲' : ' ▼')}
                </button>
              </div>

              <ul className="section-reviewers-list">
                {sortedReviewers.map((r) => (
                  <li key={r.reviewer_id} className="section-reviewer-row">
                    <div className="section-reviewer-main">
                      <div className="section-reviewer-name">{r.reviewer_full_name}</div>
                      <div className="section-reviewer-email">{r.reviewer_email}</div>
                    </div>
                    <button
                      className="delete-btn delete-btn--small"
                      onClick={() => handleDeleteSectionReviewer(r)}
                    >
                      {lang === 'ru' ? 'Удалить' : 'Delete'}
                    </button>
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <p className="loading-state">
              {lang === 'ru' ? 'Рецензенты ещё не добавлены' : 'No reviewers yet'}
            </p>
          )}
        </div>

        {/* Добавление рецензентов в секцию - через попап */}
        <div className="add-reviewer-section">
          <p className="add-reviewer-hint">
            {lang === 'ru'
              ? 'Добавьте рецензентов в секцию. Уже добавленные рецензенты не будут отображаться в списке.'
              : 'Add reviewers to the section. Reviewers already in the section will not appear in the list.'}
          </p>
          <button className="add-reviewer-btn primary" onClick={() => setShowSectionAddModal(true)}>
            {lang === 'ru' ? 'Добавить рецензента в секцию' : 'Add section reviewer'}
          </button>
        </div>
      </div>

      {/* Модальные окна */}
      {confirmData && (
        <ConfirmationModal
          message={confirmData.message}
          onConfirm={confirmData.onConfirm}
          onCancel={confirmData.onCancel}
        />
      )}

      {blockModalThesis && (
        <BlockArticleModal
          thesis={blockModalThesis}
          mode={blockModalMode}
          isLocked={blockedThesisIds.has(blockModalThesis.id)}
          stats={blockStats}
          loading={blockStatsLoading}
          error={blockStatsError}
          submitting={blockSubmitting}
          onClose={closeBlockArticleModal}
          onConfirm={submitToggleLockArticle}
          onDownload={downloadFromUrl}
          getTitle={(titleObj, primaryLanguage) => getThesisName(titleObj, primaryLanguage)}
        />
      )}

      {showBulkDownloadModal && (
        <BulkDownloadModal
          scope={bulkDownloadScope}
          onScopeChange={setBulkDownloadScope}
          reviewFilter={bulkDownloadFilter}
          onReviewFilterChange={setBulkDownloadFilter}
          fileType={bulkDownloadFileType}
          onFileTypeChange={setBulkDownloadFileType}
          submitting={bulkDownloadSubmitting}
          progress={bulkDownloadProgress}
          result={bulkDownloadResult}
          onClose={closeBulkDownloadModal}
          onConfirm={handleBulkDownload}
        />
      )}

      {showElectionModal && (
        <ElectionModal
          electionDate={electionDateInput}
          onDateChange={setElectionDateInput}
          onConfirm={confirmOpenElection}
          onCancel={() => {
            setShowElectionModal(false);
            setElectionDateInput('');
          }}
        />
      )}

      {/* Попап: добавить рецензента к конкретной работе */}
      {addModalThesisId != null && (
        <AddReviewerModal
          title={lang === 'ru' ? 'Добавить рецензента к работе' : 'Add reviewer to thesis'}
          participants={availableParticipants}
          loading={availableLoading}
          error={availableError}
          excludedIds={(thesisReviewersTable[addModalThesisId] || []).map((row) => row.reviewer_id)}
          onClose={() => setAddModalThesisId(null)}
          onSelect={async (p) => {
            await addReviewerToThesis(addModalThesisId, p.id);
            await fetchThesises();
            await loadAllThesisReviewerTables();
            setAddModalThesisId(null);
          }}
        />
      )}

      {/* Попап: добавить рецензента в секцию */}
      {showSectionAddModal && (
        <AddReviewerModal
          title={lang === 'ru' ? 'Добавить рецензента в секцию' : 'Add section reviewer'}
          participants={availableParticipants}
          loading={availableLoading}
          error={availableError}
          excludedIds={reviewers.map((r) => r.reviewer_id)}
          onClose={() => setShowSectionAddModal(false)}
          onSelect={async (p) => {
            await addReviewerToSection(p.id);
            setShowSectionAddModal(false);
          }}
        />
      )}
    </div>
  );
};

// ===== Modal components =====

const ElectionModal = ({ electionDate, onDateChange, onConfirm, onCancel }) => {
  const { getCurrentLanguage } = useLocale();
  const lang = getCurrentLanguage();

  return (
    <div className="modal-overlay">
      <div className="modal-window">
        <p className="modal-message">
          {lang === 'ru'
            ? 'Введите дату, до которой будет открыто голосование:'
            : 'Enter the date until which the election will be open:'}
        </p>
        <input
          type="date"
          value={electionDate}
          onChange={(e) => onDateChange(e.target.value)}
          className="election-date-input"
        />
        <div className="modal-buttons">
          <button className="modal-confirm-btn" onClick={onConfirm}>
            {lang === 'ru' ? 'Подтвердить' : 'Confirm'}
          </button>
          <button className="modal-cancel-btn" onClick={onCancel}>
            {lang === 'ru' ? 'Отмена' : 'Cancel'}
          </button>
        </div>
      </div>
    </div>
  );
};

const BlockArticleModal = ({
  thesis,
  mode = 'lock', // 'lock' | 'unlock'
  isLocked = false,
  stats,
  loading,
  error,
  submitting,
  onClose,
  onConfirm,
  onDownload,
  getTitle,
}) => {
  const { getCurrentLanguage } = useLocale();
  const lang = getCurrentLanguage();

  // Stats may still include legacy finalized markers; UI should rely primarily on the explicit lock state.
  const lockedFromStats = Boolean(
    stats?.is_locked ||
    stats?.workflow_locked ||
    stats?.is_finalized ||
    stats?.finalized ||
    stats?.blocked,
  );
  const lockedNow = Boolean(isLocked || lockedFromStats);

  const isUnlockMode = mode === 'unlock';
  const titleText = isUnlockMode
    ? lang === 'ru'
      ? 'Разблокировать статью'
      : 'Unlock article'
    : lang === 'ru'
      ? 'Заблокировать статью'
      : 'Lock article';

  // IMPORTANT: counts come from score-derived backend stats (not review.status)
  const approvals = Number(
    stats?.approvals ?? stats?.approved ?? stats?.approved_count ?? stats?.counts?.approved ?? 0,
  );
  const denials = Number(
    stats?.denials ??
      stats?.denied ??
      stats?.rejected ??
      stats?.rejected_count ??
      stats?.counts?.denied ??
      stats?.counts?.rejected ??
      0,
  );
  const revisionRequired = Number(
    stats?.revision_required ??
      stats?.revision_required_count ??
      stats?.counts?.revision_required ??
      0,
  );

  const payload = stats?.article_payload || stats?.article || {};
  const versions = Array.isArray(payload?.versions) ? payload.versions : [];
  const versionsCount = Number(
    stats?.versions_count ?? stats?.version_count ?? versions.length ?? 0,
  );

  const docxUrl = payload?.docx_url || payload?.urls?.docx;
  const pdfUrl = payload?.pdf_url || payload?.urls?.pdf;

  return (
    <div className="modal-overlay">
      <div className="modal-window modal-window-wide">
        <h3 className="modal-title">{titleText}</h3>

        <div className="block-modal-subtitle">
          <div className="block-modal-title">
            <strong>{(getTitle ? getTitle(thesis?.title) : '') || `#${thesis?.id}`}</strong>
          </div>

          {!isUnlockMode && (
            <div className="block-modal-note">
              {lang === 'ru'
                ? 'Это заблокирует статью: автор не сможет загружать новые версии, а рецензенты — изменять/отправлять оценки и отзывы.'
                : 'This will lock the article: the author won’t be able to upload new versions, and reviewers won’t be able to submit or change evaluations/reviews.'}
            </div>
          )}
        </div>

        {loading ? (
          <div className="loading-state">{lang === 'ru' ? 'Загрузка...' : 'Loading...'}</div>
        ) : (
          <>
            {error && <div className="error-state">{error}</div>}

            {!error && (
              <div className="block-stats">
                <div className="block-stats-row">
                  <span>{lang === 'ru' ? 'Одобрено:' : 'Approved:'}</span>
                  <strong>{approvals}</strong>
                </div>
                <div className="block-stats-row">
                  <span>{lang === 'ru' ? 'Отклонено:' : 'Denied:'}</span>
                  <strong>{denials}</strong>
                </div>
                <div className="block-stats-row">
                  <span>{lang === 'ru' ? 'Требуется доработка:' : 'Revision required:'}</span>
                  <strong>{revisionRequired}</strong>
                </div>
                <div className="block-stats-row">
                  <span>{lang === 'ru' ? 'Версий:' : 'Versions:'}</span>
                  <strong>{versionsCount}</strong>
                </div>
              </div>
            )}

            {(docxUrl || pdfUrl) && (
              <div className="block-downloads">
                <button
                  type="button"
                  className="modal-cancel-btn"
                  disabled={!docxUrl}
                  onClick={() => onDownload(docxUrl, `article_${thesis?.id}.docx`)}
                >
                  {lang === 'ru' ? 'Скачать DOCX' : 'Download DOCX'}
                </button>
                <button
                  type="button"
                  className="modal-cancel-btn"
                  disabled={!pdfUrl}
                  onClick={() => onDownload(pdfUrl, `article_${thesis?.id}.pdf`)}
                >
                  {lang === 'ru' ? 'Скачать PDF' : 'Download PDF'}
                </button>
              </div>
            )}

            {versions.length > 0 && (
              <div className="block-versions">
                <div className="block-versions-title">
                  {lang === 'ru' ? 'Версии:' : 'Versions:'}
                </div>
                <div className="block-versions-list">
                  {versions
                    .slice()
                    .sort(
                      (a, b) =>
                        Number(a?.version_index ?? a?.index ?? 0) -
                        Number(b?.version_index ?? b?.index ?? 0),
                    )
                    .map((v) => {
                      const vIdx = v?.version_index ?? v?.index ?? '';
                      const vPdf = v?.pdf_url || v?.urls?.pdf;
                      if (!vPdf) return null;

                      return (
                        <button
                          key={`ver-${vIdx}`}
                          type="button"
                          className="block-version-btn"
                          onClick={() => onDownload(vPdf, `article_${thesis?.id}_v${vIdx}.pdf`)}
                        >
                          {`PDF v${vIdx}`}
                        </button>
                      );
                    })}
                </div>
              </div>
            )}

            {!isUnlockMode && lockedNow && (
              <div className="info-state block-finalized-info">
                {lang === 'ru' ? 'Статья уже заблокирована.' : 'This article is already locked.'}
              </div>
            )}

            {isUnlockMode && !lockedNow && (
              <div className="info-state block-finalized-info">
                {lang === 'ru' ? 'Статья не заблокирована.' : 'This article is not locked.'}
              </div>
            )}
          </>
        )}

        <div className="modal-buttons">
          <button className="modal-cancel-btn" onClick={onClose} disabled={submitting}>
            {lang === 'ru' ? 'Отмена' : 'Cancel'}
          </button>
          <button
            className="modal-confirm-btn"
            onClick={onConfirm}
            disabled={loading || submitting || (isUnlockMode ? !lockedNow : lockedNow)}
          >
            {submitting
              ? lang === 'ru'
                ? isUnlockMode
                  ? 'Разблокируем...'
                  : 'Блокируем...'
                : isUnlockMode
                  ? 'Unlocking...'
                  : 'Locking...'
              : lang === 'ru'
                ? isUnlockMode
                  ? 'Разблокировать'
                  : 'Заблокировать'
                : isUnlockMode
                  ? 'Unlock'
                  : 'Lock'}
          </button>
        </div>
      </div>
    </div>
  );
};

const BulkDownloadModal = ({
  scope,
  onScopeChange,
  reviewFilter,
  onReviewFilterChange,
  fileType,
  onFileTypeChange,
  submitting,
  progress,
  result,
  onClose,
  onConfirm,
}) => {
  const { getCurrentLanguage } = useLocale();
  const lang = getCurrentLanguage();

  return (
    <div className="modal-overlay">
      <div className="modal-window modal-window-wide bulk-download-modal">
        <h3 className="modal-title">{lang === 'ru' ? 'Скачать статьи' : 'Download articles'}</h3>

        <div className="bulk-download-grid">
          <div className="bulk-download-field">
            <label className="bulk-download-label">
              {lang === 'ru' ? 'Что скачать' : 'What to download'}
            </label>
            <div className="bulk-download-options">
              <label className="bulk-download-option">
                <input
                  type="radio"
                  name="bulk-review-filter"
                  checked={reviewFilter === 'latest'}
                  onChange={() => onReviewFilterChange('latest')}
                  disabled={submitting}
                />
                <span>{lang === 'ru' ? 'Все последние версии' : 'All latest versions'}</span>
              </label>
              <label className="bulk-download-option">
                <input
                  type="radio"
                  name="bulk-review-filter"
                  checked={reviewFilter === 'approved'}
                  onChange={() => onReviewFilterChange('approved')}
                  disabled={submitting}
                />
                <span>{lang === 'ru' ? 'Только одобренные версии' : 'Only approved latest versions'}</span>
              </label>
            </div>
          </div>

          <div className="bulk-download-field">
            <label className="bulk-download-label">
              {lang === 'ru' ? 'Область выгрузки' : 'Download scope'}
            </label>
            <div className="bulk-download-options">
              <label className="bulk-download-option">
                <input
                  type="radio"
                  name="bulk-scope"
                  checked={scope === 'section'}
                  onChange={() => onScopeChange('section')}
                  disabled={submitting}
                />
                <span>{lang === 'ru' ? 'Текущая секция' : 'Current section'}</span>
              </label>
              <label className="bulk-download-option">
                <input
                  type="radio"
                  name="bulk-scope"
                  checked={scope === 'publishing'}
                  onChange={() => onScopeChange('publishing')}
                  disabled={submitting}
                />
                <span>{lang === 'ru' ? 'Весь сборник' : 'Entire publishing'}</span>
              </label>
            </div>
          </div>

          <div className="bulk-download-field">
            <label className="bulk-download-label">
              {lang === 'ru' ? 'Формат файлов' : 'File format'}
            </label>
            <div className="bulk-download-options">
              <label className="bulk-download-option">
                <input
                  type="radio"
                  name="bulk-file-type"
                  checked={fileType === 'docx'}
                  onChange={() => onFileTypeChange('docx')}
                  disabled={submitting}
                />
                <span>DOCX</span>
              </label>
              <label className="bulk-download-option">
                <input
                  type="radio"
                  name="bulk-file-type"
                  checked={fileType === 'pdf'}
                  onChange={() => onFileTypeChange('pdf')}
                  disabled={submitting}
                />
                <span>PDF</span>
              </label>
              <label className="bulk-download-option">
                <input
                  type="radio"
                  name="bulk-file-type"
                  checked={fileType === 'both'}
                  onChange={() => onFileTypeChange('both')}
                  disabled={submitting}
                />
                <span>{lang === 'ru' ? 'Оба формата' : 'Both formats'}</span>
              </label>
            </div>
          </div>
        </div>

        {submitting && (
          <div className="bulk-download-progress-wrap">
            <div className="bulk-download-progress-label">
              {lang === 'ru' ? 'Подготовка архива...' : 'Preparing archive...'} {progress || 0}%
            </div>
            <div className="bulk-download-progress-bar">
              <div className="bulk-download-progress-fill" style={{ width: `${progress || 0}%` }} />
            </div>
          </div>
        )}

        {result?.message && (
          <div className={`bulk-download-result bulk-download-result--${result.type || 'info'}`}>
            {result.message}
          </div>
        )}

        <div className="modal-buttons">
          <button className="modal-cancel-btn" onClick={onClose} disabled={submitting}>
            {lang === 'ru' ? 'Закрыть' : 'Close'}
          </button>
          <button className="modal-confirm-btn" onClick={onConfirm} disabled={submitting}>
            {submitting
              ? lang === 'ru'
                ? 'Подготавливаем...'
                : 'Preparing...'
              : lang === 'ru'
                ? 'Скачать'
                : 'Download'}
          </button>
        </div>
      </div>
    </div>
  );
};

const ConfirmationModal = ({ message, onConfirm, onCancel }) => {
  const { getCurrentLanguage } = useLocale();
  const lang = getCurrentLanguage();

  return (
    <div className="modal-overlay">
      <div className="modal-window">
        <p className="modal-message">{message}</p>
        <div className="modal-buttons">
          <button className="modal-confirm-btn" onClick={onConfirm}>
            {lang === 'ru' ? 'Подтвердить' : 'Confirm'}
          </button>
          <button className="modal-cancel-btn" onClick={onCancel}>
            {lang === 'ru' ? 'Отмена' : 'Cancel'}
          </button>
        </div>
      </div>
    </div>
  );
};

const AddReviewerModal = ({
  title,
  participants,
  loading,
  error,
  excludedIds = [],
  onClose,
  onSelect,
}) => {
  const { getCurrentLanguage } = useLocale();
  const lang = getCurrentLanguage();
  const [searchQuery, setSearchQuery] = useState('');

  const filtered = useMemo(() => {
    const q = searchQuery.toLowerCase();
    const excluded = new Set(excludedIds);
    return (participants || [])
      .filter((p) => !excluded.has(p.id))
      .filter((p) => p.fullName.toLowerCase().includes(q) || p.email.toLowerCase().includes(q));
  }, [participants, excludedIds, searchQuery]);

  return (
    <div className="modal-overlay">
      <div className="modal-window modal-window-wide">
        <h3 className="modal-title">{title}</h3>

        {loading ? (
          <div className="loading-state">{lang === 'ru' ? 'Загрузка...' : 'Loading...'}</div>
        ) : error ? (
          <div className="error-state">{error}</div>
        ) : (
          <>
            <input
              type="text"
              className="search-input"
              placeholder={lang === 'ru' ? 'Поиск рецензентов...' : 'Search reviewers...'}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <div className="modal-table-container">
              <table className="participants-table">
                <thead>
                  <tr>
                    <th>{lang === 'ru' ? 'Рецензент' : 'Reviewer'}</th>
                    <th>Email</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((p) => (
                    <tr key={p.id} onClick={() => onSelect(p)}>
                      <td>{p.fullName}</td>
                      <td>{p.email}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        <div className="modal-buttons">
          <button className="modal-cancel-btn" onClick={onClose}>
            {lang === 'ru' ? 'Отмена' : 'Cancel'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CombinedSectionManagement;
